export const bloomsTaxonomy = {
  knowledge: {
    level: 1,
    label: 'Knowledge (Remember)',
    description: 'Recall facts and basic concepts',
    color: 'bg-red-100 text-red-800',
    verbs: [
      'define', 'describe', 'identify', 'know', 'label', 'list', 'match', 'name', 
      'outline', 'recall', 'recognize', 'reproduce', 'select', 'state', 'tell', 
      'who', 'what', 'when', 'where', 'choose', 'find', 'how', 'match', 'omit', 
      'relate', 'retain', 'select', 'show', 'spell'
    ],
    examples: [
      'Define the term photosynthesis',
      'List the planets in our solar system',
      'Identify the capital cities of European countries'
    ]
  },
  comprehension: {
    level: 2,
    label: 'Comprehension (Understand)',
    description: 'Explain ideas or concepts',
    color: 'bg-orange-100 text-orange-800',
    verbs: [
      'classify', 'compare', 'contrast', 'demonstrate', 'explain', 'extend', 
      'illustrate', 'infer', 'interpret', 'outline', 'relate', 'rephrase', 
      'show', 'summarize', 'translate', 'associate', 'compute', 'convert', 
      'defend', 'discuss', 'distinguish', 'estimate', 'express', 'give examples', 
      'locate', 'predict', 'recognize', 'report', 'restate', 'review', 'tell'
    ],
    examples: [
      'Explain the process of cellular respiration',
      'Compare and contrast democracy and monarchy',
      'Summarize the main themes in the novel'
    ]
  },
  application: {
    level: 3,
    label: 'Application (Apply)',
    description: 'Use information in new situations',
    color: 'bg-yellow-100 text-yellow-800',
    verbs: [
      'apply', 'build', 'choose', 'construct', 'develop', 'experiment with', 
      'identify', 'interview', 'make use of', 'model', 'organize', 'plan', 
      'select', 'solve', 'utilize', 'calculate', 'complete', 'demonstrate', 
      'dramatize', 'employ', 'examine', 'illustrate', 'interpret', 'operate', 
      'practice', 'schedule', 'sketch', 'use', 'write'
    ],
    examples: [
      'Apply Newton\'s laws to solve this physics problem',
      'Use the scientific method to design an experiment',
      'Calculate the area of irregular shapes using integration'
    ]
  },
  analysis: {
    level: 4,
    label: 'Analysis (Analyze)',
    description: 'Draw connections among ideas',
    color: 'bg-green-100 text-green-800',
    verbs: [
      'analyze', 'break down', 'compare', 'contrast', 'diagram', 'deconstruct', 
      'differentiate', 'discriminate', 'distinguish', 'identify', 'illustrate', 
      'infer', 'outline', 'relate', 'select', 'separate', 'appraise', 'calculate', 
      'categorize', 'criticize', 'debate', 'examine', 'experiment', 'inspect', 
      'inventory', 'question', 'test'
    ],
    examples: [
      'Analyze the causes of World War I',
      'Break down the components of this chemical reaction',
      'Compare the effectiveness of different marketing strategies'
    ]
  },
  synthesis: {
    level: 5,
    label: 'Synthesis (Evaluate)',
    description: 'Justify a stand or decision',
    color: 'bg-blue-100 text-blue-800',
    verbs: [
      'appraise', 'argue', 'assess', 'attach', 'choose', 'compare', 'defend', 
      'estimate', 'evaluate', 'judge', 'predict', 'rate', 'core', 'select', 
      'support', 'value', 'critique', 'weigh', 'conclude', 'contrast', 
      'criticize', 'discriminate', 'justify', 'interpret', 'relate', 
      'summarize', 'support', 'explain'
    ],
    examples: [
      'Evaluate the effectiveness of renewable energy policies',
      'Critique the methodology used in this research study',
      'Judge the ethical implications of genetic engineering'
    ]
  },
  evaluation: {
    level: 6,
    label: 'Evaluation (Create)',
    description: 'Produce new or original work',
    color: 'bg-purple-100 text-purple-800',
    verbs: [
      'build', 'combine', 'compile', 'compose', 'construct', 'create', 'design', 
      'develop', 'devise', 'establish', 'explain', 'formulate', 'generate', 
      'invent', 'make', 'originate', 'plan', 'produce', 'role play', 'tell', 
      'write', 'adapt', 'begin', 'change', 'choose', 'collaborate', 'discuss', 
      'facilitate', 'form', 'integrate', 'modify', 'negotiate', 'rearrange', 
      'reconstruct', 'reinforce', 'reorganize', 'revise', 'structure', 
      'substitute', 'validate'
    ],
    examples: [
      'Design a sustainable city planning proposal',
      'Create a business plan for a new startup',
      'Develop a treatment protocol for a medical condition'
    ]
  }
};

// Helper functions
export const getBloomLevel = (levelKey) => {
  return bloomsTaxonomy[levelKey] || null;
};

export const getAllBloomLevels = () => {
  return Object.entries(bloomsTaxonomy).map(([key, value]) => ({ key, ...value }));
};

export const detectBloomLevel = (questionText) => {
  const text = questionText.toLowerCase();
  const levels = Object.entries(bloomsTaxonomy);
  
  // Check each level's verbs against the question text
  for (const [key, level] of levels.reverse()) { // Start from highest level
    for (const verb of level.verbs) {
      if (text.includes(verb.toLowerCase())) {
        return key;
      }
    }
  }
  
  return 'knowledge'; // Default to knowledge level
};

export const getVerbsForLevel = (levelKey) => {
  return bloomsTaxonomy[levelKey]?.verbs || [];
};

export const validateQuestionBloomAlignment = (questionText, selectedLevel) => {
  const detectedLevel = detectBloomLevel(questionText);
  const selectedLevelNum = bloomsTaxonomy[selectedLevel]?.level || 1;
  const detectedLevelNum = bloomsTaxonomy[detectedLevel]?.level || 1;
  
  return {
    isAligned: detectedLevel === selectedLevel,
    detectedLevel,
    selectedLevel,
    suggestion: detectedLevelNum !== selectedLevelNum 
      ? `Consider using verbs from ${bloomsTaxonomy[selectedLevel]?.label} level` 
      : null,
    confidence: Math.abs(detectedLevelNum - selectedLevelNum) <= 1 ? 'high' : 'low'
  };
};